import React, { useState, useEffect } from "react";
import { AiOutlineCloseCircle } from "react-icons/ai";
import Modal from './Modal'
import { localDateTimeString } from "../utils/dateUtils"
import { detectPhoneRegion, getPhoneExample, getPhonePrefix, getPhoneValidationMessage, normalizePhone, validatePersonName, validatePhone } from "../utils/validation"

export default function AddVisitorModal({ isOpen, onClose, onSubmit, defaultUnit, unitReadOnly }) {
  const phoneRegion = detectPhoneRegion()
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.matchMedia('(max-width: 768px)').matches
  })

  const [formData, setFormData] = useState({
    name: "",
    phone: getPhonePrefix(phoneRegion),
    visitingUnit: defaultUnit || "",
    purpose: "",
    expectedTime: "",
  });

  const [errors, setErrors] = useState({});

  // Sync defaultUnit into form whenever the modal opens or defaultUnit changes
  useEffect(() => {
    if (isOpen && defaultUnit) {
      setFormData((prev) => ({ ...prev, visitingUnit: defaultUnit }));
    }
  }, [isOpen, defaultUnit]);

  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({ ...prev, phone: getPhonePrefix(phoneRegion) }))
    }
  }, [isOpen, phoneRegion])

  useEffect(() => {
    const handleResize = () => {
      setIsCompact(window.matchMedia('(max-width: 768px)').matches)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  const validate = () => {
    const errs = {};
    if (!validatePersonName(formData.name)) errs.name = "Please enter a valid name";
    if (!formData.phone || formData.phone.trim() === getPhonePrefix(phoneRegion).trim()) {
      errs.phone = "Phone is required";
    } else if (!validatePhone(formData.phone, phoneRegion)) {
      errs.phone = getPhoneValidationMessage(phoneRegion);
    }
    if (!formData.visitingUnit) errs.visitingUnit = "Unit is required";
    if (!formData.purpose) errs.purpose = "Purpose is required";
    if (!formData.expectedTime) {
      errs.expectedTime = "Expected time is required";
    } else if (new Date(formData.expectedTime) < new Date()) {
      errs.expectedTime = "Expected time cannot be in the past";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    console.debug('AddVisitorModal: submitting', formData)
    try {
      const normalizedPhone = normalizePhone(formData.phone, phoneRegion)
      // Await parent's onSubmit so modal only closes after the async action completes
      if (onSubmit) await onSubmit({ ...formData, phone: normalizedPhone })
      setFormData({
        name: "",
        phone: getPhonePrefix(phoneRegion),
        visitingUnit: defaultUnit || "",
        purpose: "",
        expectedTime: "",
      })
      onClose()
    } catch (err) {
      console.error('AddVisitorModal: onSubmit error', err)
      // keep modal open so user can retry
    }
  };

  const footerStyle = {
    position: isCompact ? 'sticky' : 'relative',
    bottom: isCompact ? 0 : undefined,
    background: '#fff',
    padding: '12px 0 0',
    boxShadow: isCompact ? '0 -6px 16px rgba(15,23,42,0.06)' : 'none',
  }

  return (
    <Modal title="Add New Visitor" onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-0" style={{ width: '100%' }}>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm mb-1 text-gray-700">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              autoComplete="name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm mb-1 text-gray-700">Phone</label>
            <p className="text-xs text-gray-400 mt-1 mb-2">Example: {getPhoneExample(phoneRegion)}</p>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder={getPhoneExample(phoneRegion)}
              inputMode="tel"
              autoComplete="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-sm mb-1 text-gray-700">Visiting Unit</label>
            <input
              type="text"
              value={formData.visitingUnit}
              onChange={(e) => !unitReadOnly && setFormData({ ...formData, visitingUnit: e.target.value })}
              readOnly={!!unitReadOnly}
              autoComplete="off"
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${unitReadOnly ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
            />
            {errors.visitingUnit && <p className="text-xs text-red-500">{errors.visitingUnit}</p>}
          </div>

          <div>
            <label className="block text-sm mb-1 text-gray-700">Purpose of Visit</label>
            <input
              type="text"
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              autoComplete="off"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {errors.purpose && <p className="text-xs text-red-500">{errors.purpose}</p>}
          </div>

          <div>
            <label className="block text-sm mb-1 text-gray-700">Expected Time</label>
            <input
              type="datetime-local"
              value={formData.expectedTime}
              min={localDateTimeString()}
              onChange={(e) => setFormData({ ...formData, expectedTime: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-400 mt-1">Must be today or a future date &amp; time</p>
            {errors.expectedTime && (
              <p className="text-xs text-red-500">{errors.expectedTime}</p>
            )}
          </div>

        </div>
        <div style={{ ...footerStyle, padding: 0 }}>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border rounded py-2 text-gray-700"
              style={{ minWidth: 0 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white rounded py-2"
              style={{ minWidth: 0 }}
            >
              Add Visitor
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
